import { useCallback, useEffect, useState } from 'react';
import type { CategoryGroup } from './useCategoryGroups';

const STORAGE_KEY = 'trip-explorer:enabled-subcategories';

const computeDefaults = (groups: Array<CategoryGroup>): Set<string> => {
  const set = new Set<string>();
  for (const group of groups) {
    if (group.enabledByDefault) {
      for (const c of group.categories) set.add(c.id);
    }
  }
  return set;
};

const readStored = (): Array<string> | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return null;
  }
};

const writeStored = (ids: Set<string>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Quota or disabled storage — silently ignore. Selection will reset on
    // next load, which is acceptable.
  }
};

export interface EnabledSubcategoriesApi {
  enabled: Set<string>;
  /** True until groups are available and the initial value is resolved. */
  isReady: boolean;
  setEnabled: (next: Set<string>) => void;
  toggle: (id: string) => void;
  setGroupEnabled: (group: CategoryGroup, enabled: boolean) => void;
  resetToDefaults: () => void;
}

export const useEnabledSubcategories = (
  groups: Array<CategoryGroup> | null,
): EnabledSubcategoriesApi => {
  // Track the persisted selection separately from `groups` so that the first
  // render with groups available picks up any localStorage value without a
  // round-trip.
  const [enabled, setEnabledState] = useState<Set<string>>(() => new Set());
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!groups) return;
    const stored = readStored();
    const initial = stored ? new Set(stored) : computeDefaults(groups);
    setEnabledState(initial);
    setIsReady(true);
  }, [groups]);

  const commit = useCallback((next: Set<string>) => {
    setEnabledState(next);
    writeStored(next);
  }, []);

  const setEnabled = useCallback(
    (next: Set<string>) => {
      commit(new Set(next));
    },
    [commit],
  );

  const toggle = useCallback((id: string) => {
    setEnabledState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeStored(next);
      return next;
    });
  }, []);

  const setGroupEnabled = useCallback((group: CategoryGroup, on: boolean) => {
    setEnabledState((prev) => {
      const next = new Set(prev);
      for (const c of group.categories) {
        if (on) next.add(c.id);
        else next.delete(c.id);
      }
      writeStored(next);
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    if (!groups) return;
    commit(computeDefaults(groups));
  }, [groups, commit]);

  return { enabled, isReady, setEnabled, toggle, setGroupEnabled, resetToDefaults };
};
